/**
 * List pages для Підпорції 1.3 (2026-05-25).
 * Editor + print-view — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  modernization,
  internalTransfer,
  type FixedAssetModernization,
  type AssetInternalTransfer,
} from '@/api/asset_movement';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function ModernizationPage() {
  const list = useQuery({
    queryKey: ['modernization'],
    queryFn: () => modernization.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<FixedAssetModernization>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '') },
      { key: 'fixed_asset_id', header: 'Актив',
        sortValue: (r) => r.fixed_asset_id ?? '', cell: (r) => r.fixed_asset_id },
      { key: 'primary_cost_before', header: 'Вартість до', width: 150, align: 'right',
        sortValue: (r) => Number(r.primary_cost_before), cell: (r) => fmt2(r.primary_cost_before) },
      { key: 'total_capitalized', header: 'Капіталізовано', width: 160, align: 'right',
        sortValue: (r) => Number(r.total_capitalized), cell: (r) => fmt2(r.total_capitalized) },
      { key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні') },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Модернізація ОЗ</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Усього: {list.data?.total ?? 0}
        </span>
      </div>
      <DataTable<FixedAssetModernization>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function InternalTransferPage() {
  const list = useQuery({
    queryKey: ['internal-transfer'],
    queryFn: () => internalTransfer.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<AssetInternalTransfer>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true,
        sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      { key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '') },
      { key: 'transfer_reason', header: 'Причина',
        sortValue: (r) => r.transfer_reason ?? '', cell: (r) => r.transfer_reason },
      { key: 'items', header: 'Позицій', width: 110, align: 'right',
        sortValue: (r) => r.items?.length ?? 0, cell: (r) => r.items?.length ?? 0 },
      { key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні') },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Переміщення активів (ОЗ-3)</h2>
      </div>
      <DataTable<AssetInternalTransfer>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => {
          if (r.id && r.posted) {
            window.open(internalTransfer.printViewUrl(r.id), '_blank');
          }
        }}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
